import {
  Box,
  Flex,
  Heading,
  IconButton,
  Input,
  CloseButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  useDisclosure,
  Button,
  Text,
  InputGroup,
  InputRightElement,
  Link,
  ModalHeader,
  ModalFooter,
  VStack,
  SimpleGrid,
  Table,
  Thead,
  Th,
  Tbody,
  Tr,
  Td,
  ButtonGroup,
  Badge,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { crm, Pill } from "../../../theme";
import { DateTime } from "luxon";
import { useRef, useState } from "react";
import { FaCheck, FaDownload } from "react-icons/fa";
import {
  updateProspect,
  uploadProspect,
} from "../../../services/prospectService";
import UploadButton from "../../../components/UploadButton";
import { downloadFile } from "../../../services/documentService";

const ProspectModal = ({
  isOpen,
  onClose,
  p,
  get,
}: {
  p: any;
  isOpen: boolean;
  onClose: () => void;
  get: () => Promise<any>;
}) => {
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const [file, setFile] = useState<File>();
  const [loadingFile, setLoadingFile] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const inputRef = useRef(null);

  const download = async (p: any) => {
    setLoadingDownload(true);
    var file = await downloadFile(p.documentPath);
    const linkSource = `data:application/pdf;base64,${file}`;
    const downloadLink = document.createElement("a");
    downloadLink.href = linkSource;
    downloadLink.download = `Prospect ${p.number}`;
    downloadLink.click();
    setLoadingDownload(false);
  };
  const uploadFile = async (prospectId: string, file: File) => {
    setLoadingFile(true);
    if (file) {
      try {
        const response = await uploadProspect(prospectId, file);
        if (response) {
          toast({
            title: "Upload realizado.",
            status: "success",
            duration: 4000,
            isClosable: true,
          });
          await get();
        }
      } catch (error: any) {
        toast({
          title: "Erro ao realizar upload.",
          description: error?.response?.data ?? undefined,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setLoadingFile(false);
      }
    }
  };

  const update = async (p: any) => {
    setLoading(true);
    toast({
      title:
        p.status === "Ativo" ? "Prospect aprovado!" : "Prospect rejeitado.",
      status: "success",
      duration: 4000,
      isClosable: true,
    });

    await updateProspect(p);
    await get();

    setLoading(false);
  };
  return (
    <Modal
      scrollBehavior="inside"
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="5xl"
    >
      <ModalOverlay backdropFilter="blur(2px)" bg="blackAlpha.600" />
      <ModalContent borderRadius="16px" boxShadow="0 12px 32px rgba(16,24,40,0.18)">
        <ModalHeader>
          <Flex justifyContent="space-between">
            <Heading size="md" fontWeight="semibold">
              {p.clientName}
            </Heading>
            <CloseButton onClick={onClose} />
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Box mb="32px">
            <Flex justifyContent="space-between">
              <Box>
                <Heading size="md" fontWeight="semibold">
                  Data de abertura:
                </Heading>
                <Heading size="md" fontWeight="normal">
                  {DateTime.fromISO(p.createdDate).toFormat("dd/LL/yyyy")}
                </Heading>
              </Box>
              <Box>
                <Heading size="md" mb="12px">
                  Estados
                </Heading>
                <Flex gap="8px">
                  <Heading size="md" fontWeight="normal">
                    {p.company.states?.split(";").join(", ")}
                  </Heading>
                </Flex>
              </Box>
              <Box>
                <Heading size="md" mb="12px">
                  Segmento
                </Heading>
                <Flex flexDir="column" gap="8px">
                  <Heading size="md" fontWeight="normal">
                    {p.company.segment?.split(";").join(", ")}
                  </Heading>
                </Flex>
              </Box>
            </Flex>
          </Box>
          <Flex gap="64px">
            <Box>
              <Heading size="md" mb="12px">
                Contatos
              </Heading>
              {p.company.contacts?.map((c: any) => (
                <VStack spacing={4} align="stretch" mb="32px">
                  <Box>
                    <Heading size="sm" fontWeight="semibold">
                      Nome:
                    </Heading>
                    <Heading size="md" fontWeight="normal">
                      {c.name}
                    </Heading>
                  </Box>
                  <Box>
                    <Heading size="sm" fontWeight="semibold">
                      Cargo:
                    </Heading>
                    <Heading size="md" fontWeight="normal">
                      {c.position}
                    </Heading>
                  </Box>
                  <Box>
                    <Heading size="sm" fontWeight="semibold">
                      E-mail:
                    </Heading>
                    <Heading size="md" fontWeight="normal">
                      {c.email}
                    </Heading>
                  </Box>
                  <Box>
                    <Heading size="sm" fontWeight="semibold">
                      Telefone:
                    </Heading>
                    <Heading size="md" fontWeight="normal">
                      {c.telefone}
                    </Heading>
                  </Box>
                  <Box>
                    <Heading size="sm" fontWeight="semibold">
                      Celular:
                    </Heading>
                    <Heading size="md" fontWeight="normal">
                      {c.celular}
                    </Heading>
                  </Box>
                </VStack>
              ))}
            </Box>
            {loading ? (
              <Flex
                w="100%"
                h="100%"
                justifyContent="center"
                alignItems="center"
                minH="400px"
              >
                <Spinner />
              </Flex>
            ) : (
              <Box ml="32px">
                <Heading size="md" my="12px">
                  Propostas
                </Heading>
                <SimpleGrid columns={3} spacing={4} mb={4}>
                  <Box p={4} borderWidth={1} borderColor={crm.border} borderRadius={crm.radius} boxShadow={crm.shadowSm} bg={crm.surface}>
                    <Heading size="sm" color={crm.success}>
                      Propostas Ativas
                    </Heading>
                    <Text fontSize="xl" color={crm.text}>
                      {
                        p.prospects.filter((p: any) => p.status === "Ativo")
                          .length
                      }
                    </Text>
                  </Box>
                  <Box p={4} borderWidth={1} borderColor={crm.border} borderRadius={crm.radius} boxShadow={crm.shadowSm} bg={crm.surface}>
                    <Heading size="sm" color={crm.warning}>
                      Propostas Pendentes
                    </Heading>
                    <Text fontSize="xl" color={crm.text}>
                      {
                        p.prospects.filter((p: any) => p.status === "Pendente")
                          .length
                      }
                    </Text>
                  </Box>
                  <Box p={4} borderWidth={1} borderColor={crm.border} borderRadius={crm.radius} boxShadow={crm.shadowSm} bg={crm.surface}>
                    <Heading size="sm" color={crm.danger}>
                      Propostas Inativas
                    </Heading>
                    <Text fontSize="xl" color={crm.text}>
                      {
                        p.prospects.filter((p: any) => p.status === "Inativo")
                          .length
                      }
                    </Text>
                  </Box>
                </SimpleGrid>
                <Box border={`1px solid ${crm.border}`} borderRadius={crm.radius} boxShadow={crm.shadowSm} overflow="hidden" mb="32px">
                <Table>
                  <Thead bg={crm.subtle}>
                    <Th borderColor={crm.border} textTransform="none" color={crm.textMuted} fontWeight="700">Nº da proposta</Th>
                    <Th borderColor={crm.border} textTransform="none" color={crm.textMuted} fontWeight="700">Status</Th>
                    <Th borderColor={crm.border} textTransform="none" color={crm.textMuted} fontWeight="700">Data da aprovação</Th>
                    {/* <Th>PO</Th> */}
                    <Th borderColor={crm.border}></Th>
                  </Thead>
                  <Tbody>
                    {p.prospects?.map((pp: any) => (
                      <Tr>
                        <Td borderColor={crm.border}>Prop. {pp.number}</Td>
                        <Td borderColor={crm.border}>
                          <Pill
                            bg={pp.status === "Ativo" ? "#dcfce7" : pp.status === "Inativo" ? "#fee2e2" : "#fef9c3"}
                            color={pp.status === "Ativo" ? crm.success : pp.status === "Inativo" ? crm.danger : "#92400e"}
                          >
                            {pp.status}
                          </Pill>
                        </Td>
                        <Td borderColor={crm.border}>
                          {pp.approvedDate &&
                            DateTime.fromISO(pp.approvedDate).toFormat(
                              "dd/LL/yyyy HH:mm"
                            )}
                        </Td>
                        {/* <Td>PO 0003</Td>*/}
                        <Td borderColor={crm.border}>
                          <ButtonGroup>
                            <IconButton
                              variant="ghost"
                              color={crm.success}
                              aria-label="Aprovar"
                              onClick={() => update({ ...pp, status: "Ativo" })}
                              icon={<FaCheck />}
                              _hover={{ bg: "#dcfce7" }}
                            />

                            <IconButton
                              variant="ghost"
                              color={crm.danger}
                              aria-label="Rejeitar"
                              onClick={() =>
                                update({ ...pp, status: "Inativo" })
                              }
                              icon={<CloseButton />}
                              _hover={{ bg: "#fee2e2" }}
                            />
                            <UploadButton
                              isLoading={loadingFile}
                              inputRef={inputRef}
                              file={file}
                              setFile={setFile}
                              accept="application/pdf"
                              onFileChange={(e) => uploadFile(pp.id, e)}
                              w="100%"
                              py="20px"
                            />
                            <IconButton
                              isLoading={loadingDownload}
                              isDisabled={!pp.documentPath}
                              variant="ghost"
                              color={crm.brand}
                              onClick={() => download(pp)}
                              aria-label="Baixar"
                              icon={<FaDownload />}
                              _hover={{ bg: crm.brandSoft }}
                            />
                          </ButtonGroup>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                </Box>
              </Box>
            )}
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
export default ProspectModal;
